import { extend, isUndefined, defaults, has, each, isEmpty } from 'underscore'
import deepExtend from 'deep-extend'
import { getDomFields } from '@/common/util/dom'
import Events from '@/configuration/Events'
import whitelistedFields from '@/configuration/sources/WhitelistedFields.yml'
import fieldBaseConfig from '@/configuration/sources/FieldBaseConfiguration.yml'
import identityTypes from '@/configuration/sources/IdentityTypes.yml'
import currencyCountry from '@/configuration/sources/CurrencyCountry.yml'
import { isWallet, getTokenHelper } from '@/host/service/DNA/Helper/wallet.js'
import { MethodDescriptor } from '@/common/model/PaymentMethod'
import paymentMethodsConf from '@/configuration/sources/smartform/paymentMethodsConf.yml'
import tokensConfig from '@/configuration/sources/Tokens.yml'

export default class Reader {
  defaultBaseConfig

  constructor({ $store, $bus }) {
    this.$store = $store
    this.$bus = $bus
    this.isDebitBrand = $store.getters.isDebitBrand
    this.lastParsedFormToken = ''
  }

  parse(dna) {
    this.checkDomCoherence(dna)
    this.setDefaultIdentityDocType(dna)

    /** HACKS **/
    defaults(dna, { cards: { DEFAULT: { fields: {} } } })
    dna.cards = this.addDomFields(dna.cards)
    dna.cards = this.addMissingFieldsAsHidden(dna.cards)
    this.computeInstallmentNumberVisibility(dna.cards)
    this.removeUnsupportedTokens(dna.tokens)

    dna.cards = this.generateBrands(dna.cards)
    dna.cards = this.addPocketTypeDefaultValue(dna.cards)

    this.setPaymentMethods(
      dna.cards,
      dna.smartForm || {},
      dna.tokens,
      dna.preferredPaymentMethods
    )
    dna.cards = this.generateTokens(dna.cards, dna.tokens)

    this.computeDefaultBrandFieldsVisibility(dna.cards) // Must be done after the brand inheritance
    this.isDynamic(dna.cards)
    this.lastParsedFormToken = this.$store.state.formToken
    return dna
  }

  /**
   * If kr-embedded is found alone and DNA does not have cards, raise
   * an error. If any smartform is found then it can handle the cards form.
   *
   * @since KJS-2514
   */
  checkDomCoherence(dna) {
    if (isUndefined(dna.cards) && !this.$store.getters.hasSmartElements()) {
      this.$store.dispatch('error', 'CLIENT_508')
      this.$bus.$emit(Events.krypton.data.errorAlert)
    }
  }

  setDefaultIdentityDocType(dna) {
    this.defaultBaseConfig = fieldBaseConfig

    const country = dna.country ?? currencyCountry[dna.currency]
    if (identityTypes[country]) {
      this.defaultBaseConfig.identityDocumentType.values =
        identityTypes[country]
    }
  }
  generateBrands(cards) {
    for (const brand in cards) {
      const brandConf = JSON.parse(JSON.stringify(cards[brand]))
      let extFieldConf = brandConf

      // If it has copyFrom attribute, extend the config
      if (brandConf.copyFrom) {
        const parentBrand = brandConf.copyFrom.replace('cards.', '')
        let baseConf = JSON.parse(JSON.stringify(cards[parentBrand]))
        if (!brandConf.fields) brandConf.fields = {}
        extFieldConf = deepExtend(baseConf, brandConf)
        delete extFieldConf.copyFrom

        // Don't extend values
        for (const field in extFieldConf.fields) {
          if (!brandConf.fields) continue
          const fieldConf = extFieldConf.fields[field]
          if (fieldConf.values && brandConf.fields[field]?.values) {
            extFieldConf.fields[field].values = brandConf.fields[field].values
          }
        }
      }

      this.purgeNonWhitelistedFields(extFieldConf)
      cards[brand] = extFieldConf
    }

    return cards
  }

  generateTokens(cards, tokens) {
    let formMode = 'default'
    let paymentMethodToken = null

    // Any card or payment method has a token
    let cardsHaveToken = tokens?.cards?.some(card => card.token)
    const smartFormHasTokens = tokens?.smartForm?.some(pm => pm.token)
    if (cardsHaveToken || smartFormHasTokens) {
      // Cards - filter the invalid tokens (brand must be in cards)
      tokens.cards = tokens.cards?.filter(card => has(cards, card.brand)) ?? []
      // Cards - extend from the main config
      for (const pos in tokens.cards) {
        const card = tokens.cards[pos]
        // deep copy because in some cases the reference can change concurrently leading to unexpected behaviour see KJS-4194
        const brandConf = JSON.parse(JSON.stringify(cards[card.brand] || {}))

        // Set the token config in the main dna - forced token
        if (tokens.cards.length === 1 && tokens.forced) {
          // Extend the DNA brand conf
          for (const field in card.fields) {
            if (!brandConf.fields[field]) brandConf.fields[field] = {}
            brandConf.fields[field] = extend(
              JSON.parse(JSON.stringify(brandConf.fields[field])),
              JSON.parse(JSON.stringify(card.fields[field]))
            )
          }
        }

        // Extend the token config from the main dna - KJS-4049
        for (const field in brandConf.fields) {
          const parentConf = JSON.parse(JSON.stringify(brandConf.fields[field]))
          if (!card.fields[field]) {
            parentConf.inherit = true // The field should remain enabled, even if it has a value
            tokens.cards[pos].fields[field] = parentConf
          } else {
            const tokenConf = JSON.parse(JSON.stringify(card.fields[field]))
            if (parentConf.value && !tokenConf.value) parentConf.inherit = true
            tokens.cards[pos].fields[field] = extend(parentConf, tokenConf)
          }
        }

        // Set the config again in the main dna object
        cards[card.brand] = brandConf
      }

      // Recheck if the cards have token
      cardsHaveToken = tokens?.cards?.some(card => card.token)
      if (cardsHaveToken || smartFormHasTokens) {
        // If the cards have no token, they are no valid
        formMode = tokens.forced ? 'token' : 'wallet'

        // If the token is forced, we set it
        if (tokens.forced && cardsHaveToken) {
          paymentMethodToken = tokens.cards[0].token
        }
      }
    }

    this.$store.dispatch('update', { formMode })

    if (!isEmpty(this.$store.state.forms)) {
      each(this.$store.state.forms, formId => {
        const previousPmt =
          this.$store?.state[`cardForm_${formId}`]?.nonSensitiveValues
            ?.paymentMethodToken
        const previousPmtIsValid = getTokenHelper(tokens?.cards, previousPmt)
        if (!tokens?.forced && previousPmtIsValid) {
          paymentMethodToken = previousPmt
        }
        if (paymentMethodToken !== previousPmt)
          this.$store.dispatch(`cardForm_${formId}/updateNonSensitiveValue`, {
            paymentMethodToken
          })
      })
    }
    this.$store.dispatch('addFormPreset', {
      action: 'updateNonSensitiveValue',
      params: { paymentMethodToken }
    })

    return cards
  }

  /** SmartForm listed payment methods */
  setPaymentMethods(
    cards,
    smartForm,
    tokens = undefined,
    preferredMethods = []
  ) {
    let paymentMethods = []

    // Add smartform payment methods
    if (smartForm && Object.keys(smartForm).length) {
      paymentMethods = Object.keys(smartForm).sort((a, b) => {
        const rankA = smartForm[a].rank + 1 || Infinity
        const rankB = smartForm[b].rank + 1 || Infinity
        return rankA - rankB
      })

      this._sortAlmaEntries(paymentMethods)
    } else if (cards && Object.keys(cards).length) paymentMethods = ['CARDS']

    // Whitelist - allowed methods
    const { isMethodWhitelisted } = this.$store.getters
    paymentMethods = paymentMethods.filter(method =>
      isMethodWhitelisted(method)
    )

    // For WebView, disable popup/tab methods
    if (this.$store.state.isWebView)
      paymentMethods = paymentMethods.filter(
        method =>
          smartForm[method]?.allowIFrame ||
          paymentMethodsConf.webViewSupportedMethods.includes(method)
      )

    this.$store.dispatch('setPaymentMethods', {
      paymentMethods,
      preferredMethods
    })
    this.$store.dispatch('setCardBrands', cards ? Object.keys(cards) : [])

    const { isSinglePaymentButton } = this.$store.getters
    const { selectedMethod } = this.$store.state.smartForm

    // if the previous formToken was the noToken, allow the override since KJS-5079
    if (
      isSinglePaymentButton &&
      isWallet(tokens) &&
      !isMethodAvailable({
        paymentMethods,
        tokens,
        selectedMethod,
        formToken: this.lastParsedFormToken
      })
    ) {
      const newSelectedMethod = `CARDS:${tokens.cards[0].token}`
      this.$store.dispatch('selectMethod', newSelectedMethod)
    }
    if (!paymentMethods?.length)
      this.$store.dispatch('update', { smartForm: { hidden: true } })
  }

  isDynamic(cards) {
    for (const brand in cards) {
      const brandConf = cards[brand]
      if (
        !!brandConf?.fields?.installmentNumber?.values?.DYNAMIC ||
        !!brandConf?.fields?.firstInstallmentDelay?.values?.DYNAMIC
      ) {
        this.$store.dispatch('update', { hasDynamicValues: true })
        return
      }
    }

    this.$store.dispatch('update', { hasDynamicValues: false })
  }

  purgeNonWhitelistedFields(brandConf) {
    // Check possible alias beforehand
    if (!brandConf.fields) return brandConf

    for (const alias in whitelistedFields.alias) {
      const fieldName = whitelistedFields.alias[alias]
      if (alias in brandConf.fields) {
        brandConf.fields[fieldName] = brandConf.fields[alias]
        delete brandConf.fields[alias]
      }
    }
    for (const field in brandConf.fields) {
      if (!~whitelistedFields.fields.indexOf(field)) {
        delete brandConf.fields[field]
      }
    }

    return brandConf
  }

  /**
   * HACK : Add missing fields in case the token is updated later
   */
  addMissingFieldsAsHidden(cards) {
    if (isUndefined(cards))
      return { DEFAULT: { fields: this.defaultBaseConfig } }

    const orderedFields = {}
    for (const fieldName in this.defaultBaseConfig) {
      orderedFields[fieldName] =
        cards.DEFAULT.fields[fieldName] || this.defaultBaseConfig[fieldName]
    }
    cards.DEFAULT.fields = orderedFields
    return cards
  }

  /**
   * HACK: Checks if field should be hidden
   */
  computeInstallmentNumberVisibility(cards) {
    /**
     * HACK: Credomatic (costa rica) exception - 1 installment visible when it has interests
     * Look for an installment value with > 1 on the third position (interests)
     */
    let oneInstallmentVisible = false
    for (const brand in cards) {
      if (oneInstallmentVisible) break
      const fieldsConf = cards[brand].fields
      if (fieldsConf?.installmentNumber) {
        let values = fieldsConf.installmentNumber.values || {}
        for (const key in values) {
          const value = values[key]
          const optVals = value.split(':')
          if (parseInt(optVals[2]) > 1) {
            oneInstallmentVisible = true
            break
          }
        }
      }
    }
    /**
     * HACK: installmentNumber visibility - if installments has only one value and none of the others
     * has more than 1, hide it
     */
    let anyBrandHasInstallments = false
    for (const brand in cards) {
      const fieldsConf = cards[brand].fields
      if (fieldsConf?.installmentNumber) {
        let values = fieldsConf.installmentNumber.values || {}
        if (Object.keys(values).length > 1) {
          anyBrandHasInstallments = true
          break
        }
      }
    }
    if (anyBrandHasInstallments || oneInstallmentVisible) return
    /**
     * If none of the other brands has installments, and it's not credomatic with interests, in case
     * there is only one installment, hide the field
     */
    for (const brand in cards) {
      const fieldsConf = cards[brand].fields
      if (fieldsConf?.installmentNumber) {
        // If installmentNumber has only 1 value, remove it and hide the field
        let values = fieldsConf.installmentNumber.values || {}
        if (Object.keys(values).length <= 1) {
          fieldsConf.installmentNumber.required = false
          fieldsConf.installmentNumber.hidden = true
          if (brand !== 'DEFAULT') {
            this.setBrandFallback(fieldsConf.installmentNumber.values, brand)
            delete fieldsConf.installmentNumber
          }
        }
      }
    }
  }

  // HACK: If any of the fields is visible in the brands (and has values) show the field by default
  computeDefaultBrandFieldsVisibility(cards) {
    for (const brand in cards) {
      const fieldsConf = cards[brand].fields
      for (const fieldName in fieldsConf) {
        const fieldConf = fieldsConf[fieldName]
        // Current field is visible and has content
        const isFieldVisible =
          fieldConf.hidden === false &&
          (!fieldConf.values || Object.keys(fieldConf.values).length > 1)
        // It's hidden
        const defaultIsHidden = cards.DEFAULT.fields[fieldName].hidden === true
        // If the field is visible but default one is hidden, show it
        if (isFieldVisible && defaultIsHidden) {
          cards.DEFAULT.fields[fieldName].hidden = false
        }
      }
    }
  }

  // HACK KJS-3836 filter not supported methods.
  removeUnsupportedTokens(tokens) {
    const { paymentMethodWhitelist } = paymentMethodsConf.walletTokens

    if (tokens?.smartForm) {
      tokens.smartForm = tokens.smartForm.filter(token =>
        paymentMethodWhitelist.includes(token.paymentMethodType)
      )
    }
  }

  // HACK KJS-1566: 1 option + brand fallback - use global brand fallback
  // instead of by installment value
  setBrandFallback(values, brand) {
    if (!values) return
    const installData = values[Object.keys(values)[0]]
    const opts = installData.split(':')
    if (opts[4]) {
      this.$store.dispatch('addFormPreset', {
        action: 'setBrandFallback',
        params: { [brand]: opts[4] }
      })
    }
  }

  // HACK KJS-6311: Add pocketType default value in the DNA list if it is not present in the configuration
  addPocketTypeDefaultValue(cards) {
    for (const brand in cards) {
      const fieldsConf = cards[brand].fields
      if (fieldsConf?.pocketType) {
        let values = fieldsConf.pocketType.values || {}
        if (!values['DEFAULT']) {
          values['DEFAULT'] = '-' // Needs a value for the check in SelectField mixin
          fieldsConf.pocketType.values = values
          if (!fieldsConf.pocketType.value)
            fieldsConf.pocketType.value = 'DEFAULT'
        }
      }
    }

    return cards
  }

  // Add the fields present in the DOM to the DNA with default config
  addDomFields(cards) {
    for (const fieldName of getDomFields()) {
      if (!cards.DEFAULT.fields[fieldName]) {
        cards.DEFAULT.fields[fieldName] = this.defaultBaseConfig[fieldName]
        cards.DEFAULT.fields[fieldName].hidden = false
      }
    }

    return cards
  }

  _sortAlmaEntries(paymentMethods) {
    // Alma multiple paymentMethods -> sort them alphanumerically
    paymentMethods.sort((a, b) => {
      if (!(a.startsWith('ALMA_') && b.startsWith('ALMA_'))) return 0
      return a.localeCompare(b, undefined, {
        numeric: true,
        sensitivity: 'base'
      })
    })
  }
}

export function isMethodAvailable({
  paymentMethods,
  tokens,
  selectedMethod,
  formToken
}) {
  if (!selectedMethod) return false
  if (formToken === tokensConfig.noToken) return false
  const methodDescriptor = MethodDescriptor.create(selectedMethod)
  const activeMethod = methodDescriptor.name
  const metadata = methodDescriptor.metadata

  const cardTokens = tokens?.cards || []
  const smartFormTokens = tokens?.smartForm || []
  const allTokens = [...cardTokens, ...smartFormTokens].map(
    ({ token }) => token
  )
  return (
    (allTokens.includes(metadata) && paymentMethods.includes(activeMethod)) ||
    paymentMethods.includes(selectedMethod)
  )
}
